export type BoardParticipant = {
  peerId: string;
  displayLabel: string;
  isSelf?: boolean;
};

const PEER_COLORS = [
  "#6246ea",
  "#ef4565",
  "#2cb67d",
  "#f97316",
  "#0ea5e9",
  "#a855f7",
  "#eab308",
  "#14b8a6",
];

function peerColor(peerId: string): string {
  let h = 0;
  for (let i = 0; i < peerId.length; i++) {
    h = (h * 31 + peerId.charCodeAt(i)) | 0;
  }
  return PEER_COLORS[Math.abs(h) % PEER_COLORS.length]!;
}

type Props = {
  participants: BoardParticipant[];
  wsHint?: string | null;
  followingPeerId?: string | null;
  onFollow?: (peerId: string) => void;
  onUnfollow?: () => void;
};

export function BoardParticipants({
  participants,
  wsHint,
  followingPeerId,
  onFollow,
  onUnfollow,
}: Props) {
  if (!participants.length && !wsHint) return null;

  const followingLabel = followingPeerId
    ? participants.find((p) => p.peerId === followingPeerId)?.displayLabel
    : null;

  return (
    <div className="wb-board-participants" aria-live="polite">
      {participants.length ? (
        <ul className="wb-board-participants__list">
          {participants.map((p) => {
            const isFollowing = followingPeerId === p.peerId;
            const canFollow = !p.isSelf && onFollow;
            const chipClass = [
              "wb-board-participants__chip",
              p.isSelf ? "is-self" : "",
              isFollowing ? "is-following" : "",
              canFollow ? "is-clickable" : "",
            ]
              .filter(Boolean)
              .join(" ");

            const title = p.isSelf
              ? `${p.displayLabel} (вы)`
              : isFollowing
                ? `Следуете за ${p.displayLabel} — клик, чтобы отключить`
                : canFollow
                  ? `Следовать за видом ${p.displayLabel}`
                  : p.displayLabel;

            if (canFollow) {
              return (
                <li key={p.peerId}>
                  <button
                    type="button"
                    className={chipClass}
                    title={title}
                    aria-pressed={isFollowing}
                    onClick={() => {
                      if (isFollowing && onUnfollow) onUnfollow();
                      else onFollow(p.peerId);
                    }}
                  >
                    <span
                      className="wb-board-participants__dot"
                      style={{ background: peerColor(p.peerId) }}
                      aria-hidden
                    />
                    <span className="wb-board-participants__name">{p.displayLabel}</span>
                  </button>
                </li>
              );
            }

            return (
              <li key={p.peerId} className={chipClass} title={title}>
                <span
                  className="wb-board-participants__dot"
                  style={{ background: peerColor(p.peerId) }}
                  aria-hidden
                />
                <span className="wb-board-participants__name">
                  {p.displayLabel}
                  {p.isSelf ? " (вы)" : ""}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {followingPeerId && onUnfollow ? (
        <button
          type="button"
          className="wb-board-participants__unfollow"
          title="Перестать следовать"
          onClick={onUnfollow}
        >
          {followingLabel ? `Следите за ${followingLabel} · отключить` : "Отключить слежение"}
        </button>
      ) : null}
      {wsHint ? <span className="wb-ws-badge is-error">{wsHint}</span> : null}
    </div>
  );
}
